import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  EventEmitter,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  AbstractControl,
  AsyncValidatorFn,
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { LocalizationPipe } from '@abp/ng.core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { MultiSelectModule } from 'primeng/multiselect';
import { FormActionsComponent } from 'src/core/forms/form-actions/form-actions.component';
import { CheckboxModule } from 'primeng/checkbox';
import { Observable, of, Subject, timer } from 'rxjs';
import { catchError, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { CxInputPendingLoaderDirective } from 'src/app/shared/directives/cx-input-pending-loader.directive';
import { BaseFormComponent } from 'src/core/forms/base-form.component';
import { CrudService } from 'src/core/services/crud/crud.service';
import { FreeSwitchInstanceService } from '../../services/free-switch-instance.service';
import { TenantLookupDto } from '../../models/tenant-lookup.dto';
import { AssignedTenantDto } from '../../models/assigned-tenant.dto';
import { FreeSwitchInstanceDto } from '../../models/free-switch-instance.dto';

@Component({
  selector: 'app-freeswitch-integration-form',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    LocalizationPipe,
    DialogModule,
    InputTextModule,
    MultiSelectModule,
    CheckboxModule,
    FormActionsComponent,
    CxInputPendingLoaderDirective,
  ],
  templateUrl: './freeswitch-integration-form.component.html',
  styleUrls: ['./freeswitch-integration-form.component.scss'],
})
export class FreeSwitchIntegrationFormComponent
  extends BaseFormComponent<FreeSwitchInstanceDto>
  implements OnChanges, OnDestroy
{
  private readonly fb = inject(FormBuilder);
  private readonly crudService = inject(CrudService);
  private readonly freeSwitchInstanceService = inject(FreeSwitchInstanceService);
  private readonly destroy$ = new Subject<void>();

  @Input() visible = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<boolean>();
  @Input() isEdit = false;
  @Input() instance: FreeSwitchInstanceDto | null = null;
  @Input() instancesCount = 0;

  dialogVisible = false;
  tenants: TenantLookupDto[] = [];

  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }

  get isValidating(): boolean {
    return this.form?.pending ?? false;
  }

  get isBusy(): boolean {
    return this.isSubmitting || this.isValidating;
  }

  get tenantOptions(): (AssignedTenantDto & { name: string })[] {
    const instanceId = this.instance?.id ?? '';
    return this.tenants.map(t => ({
      freeSwitchInstanceId: instanceId,
      tenantId: t.id,
      name: t.name,
    }));
  }

  get titleKey(): string {
    return this.isEdit
      ? 'FreeswitchIntegration::EditFreeSwitchInstance'
      : 'FreeswitchIntegration::AddInstance';
  }

  get submitLabelKey(): string {
    return this.isEdit ? 'FreeswitchIntegration::Update' : 'FreeswitchIntegration::Save';
  }

  get isDefaultDisabled(): boolean {
    if (!this.isEdit && this.instancesCount === 0) {
      return true;
    }
    if (this.isEdit && this.instance?.isDefault) {
      return true;
    }
    return false;
  }

  protected buildForm(): FormGroup {
    return this.fb.group({
      name: this.fb.control('', [Validators.required, Validators.maxLength(100)]),
      ipAddress: this.fb.control('', [Validators.required]),
      webRtcHost: this.fb.control('', {
        asyncValidators: [this.webRtcHostUniqueValidator()],
        updateOn: 'change',
      }),
      assignedTenants: this.fb.control<AssignedTenantDto[]>([]),
      isDefault: this.fb.control({ value: false, disabled: false }),
    });
  }

  protected override afterFormInit(): void {
    this.crudService
      .getSimple<TenantLookupDto[]>(FreeSwitchInstanceDto, null, 'tenant-lookup')
      .pipe(takeUntil(this.destroy$))
      .subscribe({ next: list => (this.tenants = list) });

    this.form.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.cdr.markForCheck());

    this.dialogVisible = !!this.visible;
    this.applyInstanceToForm();
  }

  ngOnChanges(): void {
    if (!this.form) {
      return;
    }

    this.dialogVisible = !!this.visible;

    if (!this.visible) {
      return;
    }

    this.applyInstanceToForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onHide(): void {
    this.visibleChange.emit(false);
  }

  cancel(): void {
    this.visibleChange.emit(false);
  }

  onSave(): void {
    if (this.isValidating) {
      return;
    }
    this.onSubmit();
  }

  protected submitRequest(): Observable<unknown> {
    const raw = this.form.getRawValue();
    const assigned: AssignedTenantDto[] = (raw.assignedTenants ?? []).map(
      (t: AssignedTenantDto) => ({
        tenantId: t.tenantId,
        freeSwitchInstanceId: this.instance?.id,
      }),
    );

    const input: Partial<FreeSwitchInstanceDto> = {
      name: String(raw.name ?? '').trim(),
      ipAddress: String(raw.ipAddress ?? '').trim(),
      webRtcHost: String(raw.webRtcHost ?? '').trim() || undefined,
      isActive: true,
      assignedTenants: assigned,
      isDefault: raw.isDefault ?? false,
    };

    if (this.isEdit && this.instance) {
      return this.crudService.update<void>(FreeSwitchInstanceDto, {
        id: this.instance.id,
        ...input,
      });
    }

    return this.crudService.insertWithoutId(FreeSwitchInstanceDto, input);
  }

  protected override onSubmitSuccess(): void {
    super.onSubmitSuccess();
    this.saved.emit(!this.isEdit);
    this.visibleChange.emit(false);
  }

  private applyInstanceToForm(): void {
    if (!this.form) {
      return;
    }

    if (this.isEdit && this.instance) {
      const at = this.instance.assignedTenants;
      const assignedTenants: AssignedTenantDto[] = Array.isArray(at)
        ? at.map(t => ({
            freeSwitchInstanceId: t.freeSwitchInstanceId ?? this.instance!.id,
            tenantId: t.tenantId,
          }))
        : [];

      this.resetForm({
        name: this.instance.name,
        ipAddress: this.instance.ipAddress,
        webRtcHost: this.instance.webRtcHost ?? '',
        assignedTenants,
        isDefault: this.instance.isDefault,
      });

      if (this.isDefaultDisabled) {
        this.form.get('isDefault')?.disable({ emitEvent: false });
      } else {
        this.form.get('isDefault')?.enable({ emitEvent: false });
      }
    } else {
      this.resetForm({
        name: '',
        ipAddress: '',
        webRtcHost: '',
        assignedTenants: [],
        isDefault: this.instancesCount === 0,
      });

      if (this.instancesCount === 0) {
        this.form.get('isDefault')?.disable({ emitEvent: false });
      } else {
        this.form.get('isDefault')?.enable({ emitEvent: false });
      }
    }
  }

  private webRtcHostUniqueValidator(): AsyncValidatorFn {
    return (control: AbstractControl): Observable<ValidationErrors | null> => {
      const value = String(control.value ?? '').trim();
      if (!value) {
        return of(null);
      }

      const currentWebRtcHost = this.instance?.webRtcHost?.trim() ?? '';
      if (this.isEdit && value.toLowerCase() === currentWebRtcHost.toLowerCase()) {
        return of(null);
      }

      return timer(300).pipe(
        switchMap(() =>
          this.freeSwitchInstanceService.checkWebRtcHostUnique(value, this.instance?.id),
        ),
        map(isUnique => (isUnique ? null : { webRtcHostNotUnique: true })),
        catchError(() => of(null)),
        take(1),
      );
    };
  }
}
